import { REACTION_DISPLAY_DURATION_MS } from 'features/room-reactions/constants';
import { Reaction, ReactionItem } from 'features/room-reactions/types';
import { UserId } from 'features/users/types';
import { userReactionAdded, userReactionRemoved } from 'features/users/usersSlice';
import { SignalingSocket } from 'services/signaling';
import { store } from 'store/store';

type ReactionListener = (reactions: ReactionItem[]) => void;

export class ReactionsManagerClass {
  private listeners: Set<ReactionListener> = new Set();

  private lastReactionTimestamp: number = 0;

  private readonly THROTTLE_DELAY = 1000;

  private reactionClearTimeouts = new Map<UserId, number>();

  addListener = (listener: ReactionListener): (() => void) => {
    this.listeners.add(listener);

    return () => {
      this.listeners.delete(listener);
    };
  };

  triggerReactions = (reactions: ReactionItem[], initiatorId: UserId): void => {
    this.listeners.forEach((listener) => listener(reactions));

    if (initiatorId) {
      const { reaction } = reactions[reactions.length - 1];

      this.setUserReaction(initiatorId, reaction);
    }
  };

  sendReaction = (reaction: Reaction): void => {
    const now = Date.now();
    const timeSinceLastReaction = now - this.lastReactionTimestamp;

    if (timeSinceLastReaction < this.THROTTLE_DELAY) {
      return;
    }

    SignalingSocket.send({
      event: 'sendRoomReactions',
      data: {
        reactions: [reaction],
      },
    });

    this.lastReactionTimestamp = now;
  };

  private setUserReaction = (userId: UserId, reaction: Reaction): void => {
    const existingTimeout = this.reactionClearTimeouts.get(userId);

    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    store.dispatch(userReactionAdded({ userId, reaction }));

    const timeoutId = window.setTimeout(() => {
      store.dispatch(userReactionRemoved(userId));
      this.reactionClearTimeouts.delete(userId);
    }, REACTION_DISPLAY_DURATION_MS);

    this.reactionClearTimeouts.set(userId, timeoutId);
  };
}

export const ReactionsManager = new ReactionsManagerClass();
